import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM, { unstable_batchedUpdates } from 'react-dom';
import ReactGA from 'react-ga';
import { connect } from 'react-redux';
import { useSnackbar } from 'notistack';
import { submit, updateSyncErrors, reset, change } from 'redux-form';
import { useQuery } from '@apollo/react-hooks';

import { ACCOMMODATION } from 'graphql/queries/accommodations';
import { ACTIVITY } from 'graphql/queries/activities';
import { USER_SETTINGS_WITHOUT_SECRET_KEYS } from 'graphql/queries/settings';

import ConfirmContext from 'contexts/ConfirmContext';

import {
  WIDGET_ROOT,
  WIDGET_WRAPPER_ID,
  ADD_ONS_INDEX,
  RELATED_PRODUCTS_INDEX,
  MENU,
  ACCOMMODATION_TITLE,
  ACTIVITY_TITLE
} from 'constants/widget';

import {
  CATEGORY_PRICES,
  DAILY_PRICES,
  FIXED_PRICES
} from 'pages/widget/constants';

import {
  getReceivedAccommodationData
} from 'utils/accommodations-and-activities/prepareReceivedAccommodationData';
import { getReceivedActivityData } from 'utils/accommodations-and-activities/prepareReceivedActivityData';

import { TabPanel } from 'components';

import Header from './components/header/Header';
import Menu from './components/menu/Menu';
import GeneralAccommodationsList from './components/general-accommodations-list/GeneralAccommodationsList';
import GeneralActivitiesList from './components/general-activities-list/GeneralActivitiesList';
import ProductInfo from './components/product-info/ProductInfo';
import DateAndGuests from './components/date-and-guests/DateAndGuests';
import AddOns from './components/add-ons/AddOns';
import MandatoryFees from './components/mandatory-fees/MandatoryFees';
import Products from 'pages/widget/components/related-products/Products';
import Checkout from './components/checkout/Checkout';
import Payment from './components/payment/Payment';
import Footer from './components/footer/Footer';

import useStyles from './styles';
import { getRange } from '../../utils/book-accommodation/availableDates';
import { useAccommodationsInfiniteQuery } from './hooks/useAccommodationsInfiniteQuery';
import { useActivitiesInfiniteQuery } from './hooks/useActivitiesInfiniteQuery';
import WidgetLoader from './components/widget-loader/WidgetLoader';

const AVAILABLE_CURRENCIES = ['NOK', 'EUR', 'USD'];
const PRODUCT_SECTION_INDEX = 0;
const ADD_ONS_SECTION_INDEX = 1;
const RELATED_PRODUCT_SECTION_INDEX = 2;
const CHECKOUT_SECTION_INDEX = 3;
const PAYMENT_SECTION_INDEX = 4;

const Widget = ({
  submitForm,
  updateFormErrors,
  resetWidgetForm,
  widgetForm,
  ownerId,
  receivedAccommodationId,
  receivedActivityId,
  languages,
  logo,
  privacyPolicy,
  termsAndConditions,
  changeWidgetFormFieldValue
}) => {
  const classes = useStyles();
  const [t] = useTranslation();

  const { enqueueSnackbar } = useSnackbar();
  const confirm = React.useContext(ConfirmContext);

  const [activityDisabledDates, setActivityDisabledDates] = useState([]);

  const [productId, setProductId] = useState(receivedAccommodationId || receivedActivityId);
  const [activity, setActivity] = useState(null);
  const [receivedActivity, setReceivedActivity] = useState(null);
  const [accommodation, setAccommodation] = useState(null);
  const [receivedAccommodation, setReceivedAccommodation] = useState(
    null
  );
  const [menuIndex, setMenuIndex] = useState(0);
  const [productsMenuIndex, setProductsMenuIndex] = useState(0);

  const [lastSubmittedFormIndex, setLastSubmittedFormIndex] = useState(0);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isProductsList, setIsProductsList] = useState(!receivedAccommodationId && !receivedActivityId);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [initialSelectedCurrency, setInitialSelectedCurrency] = useState('NOK');
  const [initialSelectedLanguage, setInitialSelectedLanguage] = useState(languages[0]);
  const [initialAvailableCurrencies, setInitialAvailableCurrencies] = useState(AVAILABLE_CURRENCIES);
  const [disabledSteps, setDisabledStep] = useState([]);

  const [zoomLevel, setZoomLevel] = useState(window.devicePixelRatio);

  const { loading: settingsLoading, data: settingsData } = useQuery(
    USER_SETTINGS_WITHOUT_SECRET_KEYS,
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      variables: {
        businessOwnerId: ownerId
      }
    }
  );

  const {
    accommodationsPage,
    accommodations,
    fetchAccommodationsData,
    loading: isAccommodationsLoading,
    perPage: accommodationsPerPage
  } = useAccommodationsInfiniteQuery({
    startDate: widgetForm?.values?.range?.startDate,
    endDate: widgetForm?.values?.range?.endDate,
    guests: widgetForm?.values?.numberOfGuests || '',
    ownerId
  }, !!productId);

  const {
    activitiesPage,
    activities,
    fetchActivitiesData,
    loading: isActivitiesLoading,
    perPage: activitiesPerPage
  } = useActivitiesInfiniteQuery({
    startDate: widgetForm?.values?.range?.startDate,
    endDate: widgetForm?.values?.range?.endDate,
    guests: widgetForm?.values?.numberOfGuests || '',
    ownerId
  }, !!productId);

  const isActivitiesAccommodationsLoading = isAccommodationsLoading || isActivitiesLoading;

  const isAccommodationBooking =
    !isActivitiesAccommodationsLoading &&
    ((!!accommodations?.length && productsMenuIndex === 0) || !!receivedAccommodationId);

  const { loading: accommodationLoading, data: accommodationData} = useQuery(
    ACCOMMODATION,
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      skip: isProductsList || selectedProduct || !isAccommodationBooking || receivedActivityId,
      variables: {
        accommodationId: productId,
        businessOwnerId: ownerId
      }
    }
  );

  const { loading: activityLoading, data: activityData } = useQuery(ACTIVITY, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: isProductsList || selectedProduct || isAccommodationBooking || receivedAccommodationId,
    variables: {
      activityId: productId,
      businessOwnerId: ownerId
    }
  });

  useEffect(() => {
    if (!settingsData) return;

    if (!settingsData.payPalSettings && !settingsData.stripeSettings) {
      const payOnArrivalPossibility = settingsData.payOnArrivalPossibility;
      if (payOnArrivalPossibility && payOnArrivalPossibility.payOnArrival) {
        return;
      }
      confirm({
        title: 'Payments were not added',
        confirmationText: 'Okay'
      })
        .then(() => {
          resetWidgetForm();
          ReactDOM.unmountComponentAtNode(document.getElementById(WIDGET_ROOT));
          window.parent.document.getElementById(
            WIDGET_WRAPPER_ID
          ).style.display = 'none';
        })
        .catch(() => {});
    }
  }, [settingsData]);

  useEffect(() => {
    if (!accommodationData || !accommodationData.accommodations.length) return;

    const receivedAccommodation = accommodationData.accommodations[0];
    const data = getReceivedAccommodationData(receivedAccommodation);
    const firstPrice = receivedAccommodation.prices[0];

    if (firstPrice.participant.participant) {
      data['pricesType'] = CATEGORY_PRICES;
    } else if (firstPrice.day) {
      data['pricesType'] = DAILY_PRICES;
    } else {
      data['pricesType'] = FIXED_PRICES;
    }

    unstable_batchedUpdates(() => {
      setReceivedAccommodation(receivedAccommodation);
      setAccommodation(data);
      setDisabledStep(getDisabledSteps(data));
    })
  }, [accommodationData]);

  useEffect(() => {
    if (!activityData || !activityData.activities.length) return;

    const receivedActivity = activityData.activities[0];

    setActivityDisabledDates(
      receivedActivity.activitybookingSet
        .filter(({ isCanceled }) => !isCanceled)
        .map(({ startingDate, endingDate }) => {
          return getRange(startingDate, endingDate).map(date =>
            date.format('YYYY-MM-DD')
          );
        })
        .flat(1)
    );

    const data = getReceivedActivityData(receivedActivity);

    data['pricesType'] = receivedActivity['pricesType'];
    data['scheduleType'] = receivedActivity['scheduleType'];

    setReceivedActivity(receivedActivity);
    setActivity(data);
    setDisabledStep(getDisabledSteps(data));
  }, [activityData]);

  const onBackHandler = () => {
    if (selectedProduct) {
      unstable_batchedUpdates(() => {
        setInitialAvailableCurrencies(AVAILABLE_CURRENCIES);
        setSelectedProduct(null);
        setIsProductsList(true);
      });
      changeWidgetFormFieldValue('range', { startDate: null, endDate: null });
      changeWidgetFormFieldValue('numberOfGuests', 0);
      return;
    }

    if (menuIndex) {
      const newMenuIndex = getNextStepIncludeDisabled(menuIndex - 1, true);
      setMenuIndex(newMenuIndex);
      return;
    }

    cancelWidgetBooking();
  };

  const cancelWidgetBooking = () => {
    confirm({
      title: 'You have unsaved changes',
      description: t('Are you sure you want to cancel?'),
      confirmationText: 'Cancel',
      cancellationText: 'Back'
    })
      .then(() => {
        ReactGA.event({
          category: 'widget',
          action: `Cancel booking from widget`,
          label: 'widget'
        });
        resetWidgetForm();
        ReactDOM.unmountComponentAtNode(document.getElementById(WIDGET_ROOT));
        window.parent.document.getElementById(WIDGET_WRAPPER_ID).style.display =
          'none';
      })
      .catch(() => {});
  };

  const setMenuIndexHandler = index => {
    const CHECKOUT_SECTION = 3;
    if (
      index > lastSubmittedFormIndex ||
      (index > menuIndex && widgetForm.syncErrors)
    ) {
      enqueueSnackbar(
        t(
          'You should complete and save all previous steps before you can go to this step'
        ),
        {
          variant: 'info',
          autoHideDuration: 5000
        }
      );
      if (widgetForm.syncErrors && index !== CHECKOUT_SECTION) {
        remoteSubmitForm();
      }
      return;
    }

    if (index < menuIndex) {
      updateFormErrors();
    }

    setMenuIndex(index);
    ReactGA.event({
      category: 'widget',
      action: `go to ${getNextTabName(index)} from ${getNextTabName(
        menuIndex
      )}`,
      label: 'widget'
    });
  };

  const getConfirmButtonText = () => {
    if (selectedProduct) return 'Book';

    const PAYMENT_SECTION_INDEX = 4;
    return menuIndex === PAYMENT_SECTION_INDEX ? 'Confirm payment' : 'Next';
  };

  const getCancelButtonText = () => {
    if (selectedProduct || menuIndex) return 'Back';
    return 'Cancel';
  };

  const getNextStepIncludeDisabled = (newMenuIndex, isBack) => {
    const firstDisabledIndex = isBack ? RELATED_PRODUCTS_INDEX : ADD_ONS_INDEX;
    const secondDisabledIndex = isBack ? ADD_ONS_INDEX : RELATED_PRODUCTS_INDEX;

    if (disabledSteps.length) {
      if (
        newMenuIndex === firstDisabledIndex &&
        disabledSteps.includes(firstDisabledIndex)
      ) {
        newMenuIndex = isBack ? newMenuIndex - 1 : newMenuIndex + 1;
      }
      if (
        newMenuIndex === secondDisabledIndex &&
        disabledSteps.includes(secondDisabledIndex)
      ) {
        newMenuIndex = isBack ? newMenuIndex - 1 : newMenuIndex + 1;
      }
    }

    return newMenuIndex;
  };

  const getNextTabName = tabIndex => {
    switch (tabIndex) {
      case PRODUCT_SECTION_INDEX:
        return 'product section';
      case ADD_ONS_SECTION_INDEX:
        return 'add-ons section';
      case RELATED_PRODUCT_SECTION_INDEX:
        return 'related product section';
      case CHECKOUT_SECTION_INDEX:
        return 'checkout section';
      case PAYMENT_SECTION_INDEX:
        return 'payment section';
    }
  };

  const nextStep = () => {
    let newMenuIndex = getNextStepIncludeDisabled(menuIndex + 1);

    if (newMenuIndex > lastSubmittedFormIndex) {
      setLastSubmittedFormIndex(newMenuIndex);
    }
    setMenuIndex(newMenuIndex);

    ReactGA.event({
      category: 'widget',
      action: `go to ${getNextTabName(newMenuIndex)}`,
      label: 'widget'
    });
  };

  const remoteSubmitForm = () => {
    if (selectedProduct) {
      unstable_batchedUpdates(() => {
        setProductId(selectedProduct.id);
        setSelectedProduct(null);
        setInitialAvailableCurrencies([]);
      });
      return;
    }

    submitForm();
  };

  const selectProduct = item => {
    const listOfAvailableCurrencies = item.listOfAvailableCurrencies;

    if (!listOfAvailableCurrencies.includes(initialSelectedCurrency)) {
      setInitialSelectedCurrency(listOfAvailableCurrencies[0]);
    }

    unstable_batchedUpdates(() => {
      setInitialAvailableCurrencies(listOfAvailableCurrencies);
      setSelectedProduct(item);
      setIsProductsList(false);
    });
  };

  const getDisabledSteps = data => {
    const disabledSteps = [];

    if (!data) return [];

    const addOnsLength = data.addOnsForm.addOns.length;
    const relatedActivitiesLength = data.relatedActivitiesForm
      ? data.relatedActivitiesForm.relatedActivities.length
      : 0;
    const relatedAccommodationsLength = data.relatedAccommodationsForm
      ? data.relatedAccommodationsForm.relatedAccommodations.length
      : 0;

    if (!addOnsLength) disabledSteps.push(ADD_ONS_INDEX);
    if (!relatedActivitiesLength && !relatedAccommodationsLength) {
      disabledSteps.push(RELATED_PRODUCTS_INDEX);
    }

    return disabledSteps;
  };

  const availableSections = useMemo(() => {
    const result = [];

    if (isActivitiesAccommodationsLoading && !accommodations && !activities) {
      return result;
    }

    if (accommodations?.length) {
      result.push(ACCOMMODATION_TITLE);
    }

    if (activities?.length) {
      result.push(ACTIVITY_TITLE);
    }

    return result;
  }, [isActivitiesAccommodationsLoading, accommodations, activities])

  const loading = settingsLoading || accommodationLoading || activityLoading || isActivitiesAccommodationsLoading;

  useEffect(() => {
    const handleZoomChange = () => {
      const currentZoom = window.devicePixelRatio;
      if (currentZoom !== zoomLevel) {
        setZoomLevel(currentZoom);

        const timeout = setTimeout(() => {
          document.body.style.zoom = "100%"; 
          setZoomLevel(window.devicePixelRatio); 
        }, 6000);

        return () => clearTimeout(timeout); 
      }
    };

    window.addEventListener("resize", handleZoomChange);

    return () => {
      window.removeEventListener("resize", handleZoomChange);
    };
  }, [zoomLevel]);
  
  return (
    <div className={classes.wrapper}>
      <WidgetLoader loading={loading || isSubmit} />

      <Header
        cancelCallback={cancelWidgetBooking}
        title={loading ? "" : isAccommodationBooking ? 'Book your accommodation' : 'Book your activity'}
        initialAvailableCurrencies={initialAvailableCurrencies}
        initialSelectedCurrency={initialSelectedCurrency}
        setInitialSelectedCurrency={setInitialSelectedCurrency}
        initialSelectedLanguage={initialSelectedLanguage}
        setInitialSelectedLanguage={setInitialSelectedLanguage}
        initialAvailableLanguages={languages}
        logo={logo}
      />

      {!!productId && (
        <Menu
          items={MENU}
          disabledSteps={disabledSteps}
          isAccommodationBooking={isAccommodationBooking}
          currentIndex={menuIndex}
          setIndex={setMenuIndexHandler}
          lastSubmittedFormIndex={lastSubmittedFormIndex}
        />
      )}

      {isProductsList && !!availableSections.length && (
        <Menu
          isFilter={!!availableSections.length}
          isProductsMenu={true}
          items={availableSections.map(section => ({ title: section }))}
          currentIndex={productsMenuIndex}
          setIndex={setProductsMenuIndex}
          lastSubmittedFormIndex={lastSubmittedFormIndex}
        />
      )}

      {isProductsList && !!availableSections.length && (
        <>
          {availableSections.includes(ACCOMMODATION_TITLE) && (
            <TabPanel
              value={productsMenuIndex}
              index={0}
              className={classes.tabPanel}
            >
              <GeneralAccommodationsList
                initialSelectedCurrency={initialSelectedCurrency}
                accommodations={accommodations}
                page={accommodationsPage}
                selectProduct={selectProduct}
                fetchData={fetchAccommodationsData}
                perPage={accommodationsPerPage}
              />
            </TabPanel>
          )}
          {availableSections.includes(ACTIVITY_TITLE) && (
            <TabPanel
              value={productsMenuIndex}
              index={availableSections.includes(ACCOMMODATION_TITLE) ? 1 : 0}
              className={classes.tabPanel}
            >
              <GeneralActivitiesList
                initialSelectedCurrency={initialSelectedCurrency}
                activities={activities}
                page={activitiesPage}
                selectProduct={selectProduct}
                fetchData={fetchActivitiesData}
                perPage={activitiesPerPage}
              />
            </TabPanel>
          )}
        </>
      )}

      {!isActivitiesAccommodationsLoading && !availableSections.length && !productId && (
        <div className={classes.noItemMsg}>
          <h1>No items</h1>
        </div>
      )}

      {selectedProduct && (
        <ProductInfo
          product={selectedProduct}
          initialSelectedCurrency={initialSelectedCurrency}
        />
      )}

      {(accommodation || activity) && (
        <>
          <TabPanel value={menuIndex} index={0} className={classes.tabPanel}>
            {menuIndex === 0 && (
              <DateAndGuests
                ownerId={ownerId}
                isAccommodationBooking={isAccommodationBooking}
                initialSelectedCurrency={initialSelectedCurrency}
                initialSelectedLanguage={initialSelectedLanguage}
                activityDisabledDates={activityDisabledDates}
                onSubmit={nextStep}
                productData={accommodation || activity}
                receivedProduct={receivedAccommodation || receivedActivity}
                languages={languages}
              />
            )}
          </TabPanel>
          <TabPanel value={menuIndex} index={1} className={classes.tabPanel}>
            {menuIndex === 1 && (
              <AddOns onSubmit={nextStep} accommodationData={accommodation || activity} />
            )}
          </TabPanel>
          
          {accommodation && (
              <MandatoryFees accommodationData={accommodation} />
           )}
          
          <TabPanel value={menuIndex} index={2} className={classes.tabPanel}>
            {menuIndex === 2 && (
              <Products
                logo={logo}
                isAccommodationBooking={isAccommodationBooking}
                onSubmit={nextStep}
                productData={accommodation || activity}
                ownerId={ownerId}
              />
            )}
          </TabPanel>
          <TabPanel value={menuIndex} index={3} className={classes.tabPanel}>
            {menuIndex === 3 && (
              <Checkout
                isAccommodationBooking={isAccommodationBooking}
                onSubmit={nextStep}
                productData={accommodation || activity}
                title="Booking summary"
                productsMenuIndex={productsMenuIndex}
              />
            )}
          </TabPanel>
          <TabPanel value={menuIndex} index={4} className={classes.tabPanel}>
            {menuIndex === 4 && (
              <Payment
                isAccommodationBooking={isAccommodationBooking}
                onSubmit={() => {
                  setIsSubmit(true);
                  setLastSubmittedFormIndex(menuIndex + 1);
                  ReactGA.event({
                    category: 'widget',
                    action: `submit booking from widget`,
                    label: 'widget'
                  });
                }}
                setIsSubmit={setIsSubmit}
                isSubmit={isSubmit}
                productData={accommodation || activity}
                settingsData={settingsData}
              />
            )}
          </TabPanel>
        </>
      )}

      {!isProductsList && (
        <Footer
          menuIndex={menuIndex}
          isAccommodationBooking={isAccommodationBooking}
          isSelectedProduct={!!selectedProduct}
          cancelButton={getCancelButtonText()}
          confirmButton={getConfirmButtonText()}
          confirmCallback={remoteSubmitForm}
          cancelCallback={onBackHandler}
          accommodationData={accommodation || activity}
          isHideConfirmButton={isProductsList}
          isLastStep={menuIndex === 4}
          privacyPolicy={privacyPolicy}
          termsAndConditions={termsAndConditions}
          ownerId={ownerId}
        />
      )}
    </div>
  );
};

const mapStateToProps = state => {
  return {
    widgetForm: state.form.widgetForm
  };
};

const mapDispatchToProps = dispatch => ({
  changeWidgetFormFieldValue: (field, value) => {
    dispatch(change('widgetForm', field, value));
  },
  submitForm: () => dispatch(submit('widgetForm')),
  updateFormErrors: () => dispatch(updateSyncErrors('widgetForm', {})),
  resetWidgetForm: () => dispatch(reset('widgetForm'))
});

export default connect(mapStateToProps, mapDispatchToProps)(Widget);
